/**
 * HR / Leave Usage Audit — สรุปการใช้สิทธิ์ลารายคนทั้งปี.
 * fb#104: HR/OWNER ยกเลิกใบลาที่อนุมัติแล้วของวันที่ผ่านมาได้ทีละใบ (ให้พนักงานยื่นใหม่ให้ถูกประเภท).
 * เศษวัน/ชม./นาที คำนวณจากฝั่ง backend ด้วยตัวหารเดียวกับหน้าโควตาพนักงาน
 * (hours_per_workday_override ต่อคน) — เลขในตารางนี้ต้องตรงกับที่พนักงานเห็นเป๊ะ.
 *
 * ใช้ในแท็บ #leave ของหน้า Time hub (วางต่อจาก HRLeavePackagesContent).
 */
import { useState } from 'react';
import { Search, ListChecks, X, Download } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Loading } from '../../components/layout/Loading';
import { useLeaveUsage, useLeaveUsageRecords, useOverrideAttendanceDay, type LeaveUsageEmployee, type LeaveUsageRecord } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { useAuthStore } from '../../lib/store';

const LEAVE_TYPE_LABEL: Record<string, string> = {
  sick: 'ป่วย',
  personal: 'กิจ',
  vacation: 'พักร้อน',
  unpaid: 'ไม่รับค่าจ้าง',
  maternity: 'ลาคลอด',
};
const LEAVE_TYPE_COLUMNS = ['sick', 'personal', 'vacation', 'unpaid'] as const;
/** ชนิดที่มีเพดานสิทธิ์ (มีคงเหลือให้ดู) — unpaid ไม่มีเพดาน */
const QUOTA_TYPES = ['sick', 'personal', 'vacation'] as const;
type QuotaType = (typeof QUOTA_TYPES)[number];
const isQuotaType = (t: string): t is QuotaType => (QUOTA_TYPES as readonly string[]).includes(t);

const STATUS_LABEL: Record<string, string> = {
  approved: 'อนุมัติแล้ว',
  pending: 'รออนุมัติ',
  pending_L1: 'รออนุมัติ',
  pending_L2: 'รออนุมัติ',
  pending_L3: 'รออนุมัติ',
  conditionally_approved: 'อนุมัติแบบมีเงื่อนไข',
  cancelled: 'ยกเลิกแล้ว',
};

/** เศษวัน → ข้อความ "0.146 วัน (1 ชม. 10 นาที)" · วันเต็ม → "2 วัน" เฉยๆ */
function formatDaysHM(days: number, hoursPerDay: number): string {
  if (Number.isInteger(days)) return `${days} วัน`;
  const totalMinutes = Math.round(days * hoursPerDay * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const parts = [h > 0 ? `${h} ชม.` : '', m > 0 ? `${m} นาที` : ''].filter(Boolean).join(' ');
  return parts ? `${days} วัน (${parts})` : `${days} วัน`;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

// fb#261: HR ขอโหลดรายงานลา (ทั้งบริษัท/รายแผนก/รายคน) ไปทำรายงานต่อ — ไฟล์ = แถวที่เห็นบนจอเป๊ะ
// ข้อความขึ้นต้น = + - @ → เติม ' กัน Excel ตีเป็นสูตร (ตัวเลขติดลบ เช่น เกินสิทธิ์ ส่งมาเป็น number จึงไม่โดน)
const csvCell = (v: unknown) => {
  const t = typeof v === 'string' && /^[=+\-@\t\r]/.test(v) ? `'${v}` : String(v ?? '');
  return `"${t.replace(/"/g, '""')}"`;
};
function saveCsv(rows: unknown[][], filename: string) {
  // BOM นำหน้า ไม่งั้น Excel บน Windows อ่านภาษาไทยเพี้ยน
  const text = '\uFEFF' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface QuotaTotals {
  headcount: number;
  /** คนที่ยังไม่ได้ตั้งสิทธิ์ปีนี้ — ไม่ถูกนับในยอดสิทธิ์/คงเหลือ */
  noQuota: number;
  byType: Record<QuotaType, { quota: number; used: number; remaining: number; over: number }>;
  usedAll: number;
}

/** รวมยอดทั้งบริษัท (หรือเฉพาะคนที่กรองอยู่) */
function summarize(list: LeaveUsageEmployee[]): QuotaTotals {
  const byType = {
    sick: { quota: 0, used: 0, remaining: 0, over: 0 },
    personal: { quota: 0, used: 0, remaining: 0, over: 0 },
    vacation: { quota: 0, used: 0, remaining: 0, over: 0 },
  } as QuotaTotals['byType'];
  let noQuota = 0;
  let usedAll = 0;

  for (const e of list) {
    usedAll += e.total_days;
    if (!e.quota || !e.remaining) noQuota += 1;
    for (const t of QUOTA_TYPES) {
      byType[t].used += e.by_type[t] ?? 0;
      if (!e.quota || !e.remaining) continue;
      byType[t].quota += e.quota[t];
      // คนที่ใช้เกินสิทธิ์ (ค่าติดลบ) ไม่เอาไปหักยอด "คงเหลือ" ของคนอื่น —
      // สิทธิ์ลาโอนกันไม่ได้ นับแยกเป็น "เกินสิทธิ์" แทน
      const left = e.remaining[t];
      if (left >= 0) byType[t].remaining += left;
      else byType[t].over += -left;
    }
  }
  for (const t of QUOTA_TYPES) {
    byType[t].quota = round2(byType[t].quota);
    byType[t].used = round2(byType[t].used);
    byType[t].remaining = round2(byType[t].remaining);
    byType[t].over = round2(byType[t].over);
  }
  return { headcount: list.length, noQuota, byType, usedAll: round2(usedAll) };
}

/** แถวย่อยใต้ตัวเลข "ใช้ไป" — คงเหลือ / เกินสิทธิ์ / ยังไม่ตั้งสิทธิ์ */
function RemainingHint({ emp, type }: { emp: LeaveUsageEmployee; type: QuotaType }) {
  if (!emp.quota || !emp.remaining) {
    return <div className="text-[11px] text-gray-400">ยังไม่ตั้งสิทธิ์</div>;
  }
  const left = emp.remaining[type];
  const quota = emp.quota[type];
  if (left < 0) {
    return <div className="text-[11px] text-red-600">เกินสิทธิ์ {Math.abs(left)} วัน</div>;
  }
  return (
    <div className="text-[11px] text-gray-500">
      เหลือ <span className="font-medium text-emerald-700">{left}</span> / {quota} วัน
    </div>
  );
}

function periodLabel(r: { period: string; start_time: string | null; end_time: string | null }): string {
  if (r.period === 'custom' && r.start_time && r.end_time) return `${r.start_time}–${r.end_time}`;
  if (r.period === 'morning') return 'ครึ่งวันเช้า';
  if (r.period === 'afternoon') return 'ครึ่งวันบ่าย';
  return 'เต็มวัน';
}

function formatThaiDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00+07:00');
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}

export function HRLeaveUsageContent() {
  const year = new Date().getFullYear();
  const { data, isLoading } = useLeaveUsage(year);
  const [filter, setFilter] = useState('');
  // fb#268 กรองตามตำแหน่ง · fb#484 เพิ่มแผนก · fb#492 TN เน้นตำแหน่งเป็นหลัก → ตำแหน่งก่อน แผนกรอง
  const [dept, setDept] = useState('');
  const [pos, setPos] = useState('');
  const [detailFor, setDetailFor] = useState<LeaveUsageEmployee | null>(null);

  const employees = data?.employees ?? [];
  const positions = [...new Set(employees.map((e) => e.position).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'th'));
  const departments = [...new Set(employees.filter((e) => !pos || e.position === pos).map((e) => e.department).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'th'));
  const filtered = employees.filter((e) => {
    if (dept && e.department !== dept) return false;
    if (pos && e.position !== pos) return false;
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return e.emp_code.toLowerCase().includes(q) ||
      e.name.toLowerCase().includes(q) ||
      e.nickname.toLowerCase().includes(q);
  });

  // ยอดรวมของ "คนที่แสดงอยู่ตอนนี้" (พิมพ์ค้นหาแล้วยอดรวมขยับตาม)
  // สิทธิ์/คงเหลือรวมนับเฉพาะคนที่ตั้งสิทธิ์แล้ว — คนที่ยังไม่ตั้งไม่มีเพดานให้บวก
  // (ถ้าเอา 0 ไปบวกด้วย ยอด "คงเหลือทั้งบริษัท" จะดูน้อยกว่าความจริง)
  const totals = summarize(filtered);

  function exportUsage() {
    const header = ['รหัสพนักงาน', 'ชื่อ-นามสกุล', 'ชื่อเล่น', 'ตำแหน่ง', 'แผนก'];
    for (const t of LEAVE_TYPE_COLUMNS) {
      header.push(`${LEAVE_TYPE_LABEL[t]} ใช้ไป (วัน)`);
      if (isQuotaType(t)) header.push(`${LEAVE_TYPE_LABEL[t]} สิทธิ์ (วัน)`, `${LEAVE_TYPE_LABEL[t]} คงเหลือ (วัน)`);
    }
    header.push('รวมใช้ไป (วัน)');
    const rows = filtered.map((e) => {
      const r: unknown[] = [e.emp_code, e.name, e.nickname, e.position, e.department];
      for (const t of LEAVE_TYPE_COLUMNS) {
        r.push(e.by_type[t] ?? 0);
        if (isQuotaType(t)) r.push(e.quota ? e.quota[t] : 'ยังไม่ตั้งสิทธิ์', e.remaining ? e.remaining[t] : '');
      }
      r.push(e.total_days);
      return r;
    });
    const scope = `${pos ? `-${pos}` : ''}${dept ? `-${dept}` : ''}`;
    saveCsv([header, ...rows], `สิทธิ์และการใช้วันลา-${year + 543}${scope}.csv`);
  }

  return (
    <div className="card p-5 space-y-3 mt-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold">สิทธิ์และการใช้วันลารายคน (ปีนี้)</h3>
          <p className="text-sm text-gray-500">
            ตัวเลขบน = ใช้ไปแล้ว · บรรทัดล่าง = คงเหลือจากสิทธิ์ที่ตั้งไว้ ปี {year + 543} —
            กดดูรายละเอียดเพื่อดูใบลาทีละใบ
          </p>
        </div>
        <button type="button" onClick={exportUsage} disabled={filtered.length === 0} className="btn-secondary text-sm whitespace-nowrap">
          <Download className="w-4 h-4" /> โหลดเป็นไฟล์ Excel
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px_200px] gap-2">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            className="input pl-9"
            placeholder="ค้นหา ชื่อ / ชื่อเล่น / รหัสพนักงาน…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <select className="input" value={pos} onChange={(e) => { setPos(e.target.value); setDept(''); }} aria-label="ตำแหน่ง">
          <option value="">ทุกตำแหน่ง</option>
          {positions.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <select className="input" value={dept} onChange={(e) => setDept(e.target.value)} aria-label="แผนก">
          <option value="">ทุกแผนก</option>
          {departments.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>

      {/* ยอดรวมทั้งบริษัท — ดูภาพรวมก่อนว่าใช้ไปเท่าไหร่ เหลือเท่าไหร่ ไม่ต้องไล่ทีละคน */}
      {!isLoading && filtered.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">
              รวม {totals.headcount} คน{pos ? ` · ตำแหน่ง${pos}` : ''}{dept ? ` · แผนก${dept}` : ''}{filter.trim() ? ' (เฉพาะที่ค้นหา)' : dept || pos ? '' : 'ทั้งบริษัท'}
            </p>
            <p className="text-xs text-gray-500">ใช้ไปแล้วรวมทุกประเภท {totals.usedAll} วัน</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {QUOTA_TYPES.map((t) => {
              const v = totals.byType[t];
              return (
                <div key={t} className="bg-white rounded-lg border border-gray-200 px-3 py-2">
                  <p className="text-xs text-gray-500">{LEAVE_TYPE_LABEL[t]}</p>
                  <p className="text-lg font-bold leading-tight">
                    <span className="text-emerald-700">{v.remaining}</span>
                    <span className="text-sm font-normal text-gray-400"> / {v.quota} วัน</span>
                  </p>
                  <p className="text-[11px] text-gray-500">
                    ใช้ไป {v.used} วัน
                    {v.over > 0 && <span className="text-red-600"> · เกินสิทธิ์ {v.over} วัน</span>}
                  </p>
                </div>
              );
            })}
          </div>
          {totals.noQuota > 0 && (
            <p className="text-xs text-amber-700 mt-2">
              มี {totals.noQuota} คนยังไม่ได้ตั้งสิทธิ์การลาปีนี้ — ยอดสิทธิ์/คงเหลือด้านบนยังไม่รวมคนกลุ่มนี้
              (ตั้งได้ที่ "ตั้งค่าสิทธิ์การลา")
            </p>
          )}
        </div>
      )}

      {isLoading ? (
        <Loading />
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500">ไม่พบพนักงาน</div>
      ) : (
        <div className="rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs">
              <tr>
                <th className="px-3 py-2 text-left">พนักงาน</th>
                <th className="px-3 py-2 text-right">ป่วย</th>
                <th className="px-3 py-2 text-right">กิจ</th>
                <th className="px-3 py-2 text-right">พักร้อน</th>
                <th className="px-3 py-2 text-right">ไม่รับค่าจ้าง</th>
                <th className="px-3 py-2 text-right">รวม</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.emp_code} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-medium">{e.name}{e.nickname && <span className="text-gray-400 font-normal"> ({e.nickname})</span>}</div>
                    <div className="text-xs text-gray-500">{e.emp_code}{e.position && ` · ${e.position}`}</div>
                  </td>
                  {LEAVE_TYPE_COLUMNS.map((t) => (
                    <td key={t} className="px-3 py-2 text-right whitespace-nowrap text-xs">
                      {formatDaysHM(e.by_type[t] ?? 0, e.hours_per_day)}
                      {isQuotaType(t) && <RemainingHint emp={e} type={t} />}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right whitespace-nowrap font-medium text-xs">
                    {formatDaysHM(e.total_days, e.hours_per_day)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setDetailFor(e)} className="btn-secondary text-xs">
                      <ListChecks className="w-3.5 h-3.5" /> ดูรายละเอียด
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {/* แถวรวมท้ายตาราง — คอลัมน์ตรงกับหัวตารางเป๊ะ (ใช้ไป / คงเหลือรวม) */}
            <tfoot className="bg-gray-50 border-t-2 border-gray-200 text-xs">
              <tr>
                <td className="px-3 py-2 font-medium">รวม {totals.headcount} คน</td>
                {LEAVE_TYPE_COLUMNS.map((t) => (
                  <td key={t} className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="font-medium">
                      {isQuotaType(t) ? totals.byType[t].used : round2(
                        filtered.reduce((s, e) => s + (e.by_type[t] ?? 0), 0),
                      )} วัน
                    </div>
                    {isQuotaType(t) && (
                      <div className="text-[11px] text-gray-500">
                        เหลือ <span className="font-medium text-emerald-700">{totals.byType[t].remaining}</span>
                        {' '}/ {totals.byType[t].quota} วัน
                      </div>
                    )}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{totals.usedAll} วัน</td>
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {detailFor && (
        <LeaveUsageDetailModal emp={detailFor} year={year} onClose={() => setDetailFor(null)} />
      )}
    </div>
  );
}

/** วันนี้ตามเวลาไทย (YYYY-MM-DD) — ตัดสินว่าใบไหน "ผ่านมาแล้ว" ให้ตรงกับเซิร์ฟเวอร์ */
function todayBangkok(): string {
  return new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 10);
}

function LeaveUsageDetailModal({ emp, year, onClose }: { emp: LeaveUsageEmployee; year: number; onClose: () => void }) {
  const { data, isLoading } = useLeaveUsageRecords(emp.emp_code, year);
  const records = data?.records ?? [];
  const role = useAuthStore((st) => st.profile?.role);
  const canVoid = role === 'OWNER' || role === 'HR';
  const today = todayBangkok();
  const overrideDay = useOverrideAttendanceDay();
  const toast = useToast();
  const consequence = useConsequence();
  const [voiding, setVoiding] = useState<LeaveUsageRecord | null>(null);
  const [reason, setReason] = useState('');

  function exportRecords() {
    const hpd = data?.hours_per_day ?? 8;
    const header = ['รหัสพนักงาน', 'ชื่อ-นามสกุล', 'ตำแหน่ง', 'แผนก', 'วันที่', 'ประเภท', 'ช่วงเวลา', 'จำนวน (วัน)', 'จำนวน (ชม.)', 'สถานะ'];
    const rows = records.map((r) => [
      emp.emp_code, emp.name, emp.position, emp.department, r.date,
      LEAVE_TYPE_LABEL[r.leave_type] ?? r.leave_type, periodLabel(r), r.days, round2(r.days * hpd),
      STATUS_LABEL[r.status] ?? r.status,
    ]);
    saveCsv([header, ...rows], `ใบลา-${emp.emp_code}-${emp.name}-${year + 543}.csv`);
  }

  const voidable = (r: LeaveUsageRecord) =>
    canVoid && !!r.leave_id && (r.status === 'approved' || r.status === 'conditionally_approved') && r.date <= today;

  async function confirmVoid() {
    if (!voiding || overrideDay.isPending) return;
    const note = reason.trim();
    if (note.length < 5) return toast.error('ใส่เหตุผลอย่างน้อย 5 ตัวอักษร — จะบันทึกในประวัติและแจ้งพนักงาน');
    const ok = await consequence({
      title: 'ยกเลิกใบลา — ให้พนักงานยื่นใหม่',
      intro: 'เมื่อกดยืนยัน ใบลาใบนี้จะถูกยกเลิก วันนั้นจะไม่นับเป็นวันลาอีก จนกว่าพนักงานจะยื่นใบใหม่',
      lines: [
        { label: 'พนักงาน', value: `${emp.name} (${emp.emp_code})` },
        { label: 'วันที่', value: formatThaiDate(voiding.date) },
        { label: 'ใบลา', value: `${LEAVE_TYPE_LABEL[voiding.leave_type] ?? voiding.leave_type} · ${periodLabel(voiding)}`, emphasis: true },
        { label: 'เหตุผล', value: note },
      ],
      effects: [
        'พนักงานที่ผูก LINE ไว้จะได้รับข้อความพร้อมเหตุผล และให้ยื่นใบลาใหม่ในแอป',
        'ถ้าพนักงานไม่ยื่นใหม่และไม่มีสแกนวันนั้น ระบบจะนับเป็นขาดงาน',
        'บันทึกในประวัติการใช้งานระบบว่าใครยกเลิก เมื่อไร',
        'ยอดเงินเดือนจะเปลี่ยนเมื่อครบ 4 ขั้น: พนักงานยื่นใบใหม่ → อนุมัติใบใหม่ → รัน "สรุปเวลาเข้างาน" ใหม่ → รันร่างเงินเดือนใหม่',
      ],
      confirmText: 'ยืนยันยกเลิกใบลา',
    });
    if (!ok) return;
    overrideDay.mutate(
      { emp_code: emp.emp_code, date: voiding.date, action: 'void', leave_id: voiding.leave_id, note },
      {
        onSuccess: (r) => {
          toast.success(
            `ยกเลิกใบลาแล้ว — ต่อไป: พนักงานยื่นใหม่ → อนุมัติ → สรุปเวลาใหม่ → รันร่างเงินเดือนใหม่${r.employee_notified === false ? ' (พนักงานไม่ได้ผูก LINE จึงไม่มีข้อความแจ้ง ต้องบอกเอง)' : ''}`,
          );
          setVoiding(null);
          setReason('');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ยกเลิกใบลาไม่สำเร็จ'),
      },
    );
  }

  return (
    <Modal open onClose={onClose} title={`ใบลาของ ${emp.name} — ปี ${year + 543}`} size="max-w-2xl">
      <div className="space-y-3">
        {isLoading ? (
          <Loading />
        ) : records.length === 0 ? (
          <div className="card p-6 text-center text-sm text-gray-500">ปีนี้ยังไม่มีการลา</div>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">วันที่</th>
                  <th className="px-3 py-2 text-left">ประเภท</th>
                  <th className="px-3 py-2 text-left">ช่วงเวลา</th>
                  <th className="px-3 py-2 text-right">จำนวน</th>
                  <th className="px-3 py-2 text-left">สถานะ</th>
                  {canVoid && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-3 py-2 whitespace-nowrap">{formatThaiDate(r.date)}</td>
                    <td className="px-3 py-2">{LEAVE_TYPE_LABEL[r.leave_type] ?? r.leave_type}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{periodLabel(r)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{formatDaysHM(r.days, data?.hours_per_day ?? 8)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{STATUS_LABEL[r.status] ?? r.status}</td>
                    {canVoid && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {voidable(r) && (
                          <button
                            type="button"
                            onClick={() => { setVoiding(r); setReason(''); }}
                            className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                          >
                            ยกเลิกใบลา
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {voiding && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2 text-sm">
            <div className="font-medium text-red-900">
              ยกเลิกใบ{LEAVE_TYPE_LABEL[voiding.leave_type] ?? voiding.leave_type} วันที่ {formatThaiDate(voiding.date)}
            </div>
            <div className="text-red-900/80 text-xs">
              ใช้เมื่อใบลาผิดประเภท เช่น ยื่นช่วงที่ยังไม่ได้ตั้งสิทธิ์ลาแล้วเลือกลาไม่รับเงินเดือน — ยกเลิกแล้วให้พนักงานยื่นใหม่
            </div>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="เหตุผล (แจ้งพนักงาน) เช่น ยื่นช่วงยังไม่ตั้งสิทธิ์ ให้ยื่นลากิจใหม่"
              className="input w-full text-base"
              maxLength={200}
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setVoiding(null)} className="btn-secondary">ไม่ยกเลิก</button>
              <button type="button" onClick={confirmVoid} disabled={overrideDay.isPending} className="btn-primary bg-red-600 hover:bg-red-700">
                {overrideDay.isPending ? 'กำลังยกเลิก…' : 'ยกเลิกใบลานี้'}
              </button>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
          <button type="button" onClick={exportRecords} disabled={records.length === 0} className="btn-secondary">
            <Download className="w-4 h-4" /> โหลดเป็นไฟล์ Excel
          </button>
          <button onClick={onClose} className="btn-secondary"><X className="w-4 h-4" /> ปิด</button>
        </div>
      </div>
    </Modal>
  );
}
